/**
 * Подбор записи меты каталога (`seo-meta-catalog`) под текущую страницу.
 *
 * Логика унаследована из старого getCatalog.js: и для страницы, и для каждой
 * записи строится ключ-урл вида `farkopy/filter/marka-#VALUE#/model-audi`,
 * дальше ключи страницы перебираются от самого точного к самому общему и
 * берётся первая найденная запись — сначала своего домена, потом root.
 * Если на один ключ-урл одного домена записей несколько, берётся запись
 * с меньшей сортировкой.
 *
 * Модуль намеренно без внешних зависимостей: покрыт юнит-тестами и его удобно
 * дёргать при отладке.
 */

/** Значение «для всех»: в ключе-урле выглядит как #VALUE# */
export const ROOT_VALUE = "root";

/** Подстановка «любое значение» в ключах-урлах (формат старого раздела) */
export const URL_ANY_VALUE = "#VALUE#";

/** Домен записи, данные которой применяются ко всем доменам без своих данных */
export const ROOT_DOMAIN = "root";

/** Ключи data, которые не являются параметрами фильтра */
const NON_PARAM_DATA_KEYS = new Set([
  "legacyKey",
  "domain",
  "excludeDomains",
  "section",
  "metaTitle",
  "metaDescription",
  "h1",
  "topText",
  "bottomText",
  "links",
  "usefulInfoText",
  "usefulInfoItems",
  "lastChange",
]);

/** Параметры, которые в урле всегда идут первыми и в этом порядке */
const ORDERED_PARAM_KEYS = ["marka", "model", "kuzov"];

export type FilterValues = Record<string, string>;

export function asTrimmedString(value: unknown) {
  return String(value ?? "").trim();
}

/** Значение фильтра из redux (`{marka: ['audi']}`) → скаляр */
function toScalar(value: unknown): string {
  if (Array.isArray(value)) return asTrimmedString(value[0]);
  return asTrimmedString(value);
}

/** Текущие параметры фильтра страницы: `{marka: 'audi', tip_kryuka: 'nesemnyy'}` */
export function buildFilterValues(
  filterParams: Record<string, any>,
): FilterValues {
  const values: FilterValues = {};
  for (const [key, value] of Object.entries(filterParams || {})) {
    const scalar = toScalar(value);
    if (scalar) values[key] = scalar;
  }
  return values;
}

/** Коды параметров фильтра, заданные в записи */
function paramKeysOfRecord(data: Record<string, any>): string[] {
  return Object.keys(data || {}).filter(
    (key) => !NON_PARAM_DATA_KEYS.has(key) && asTrimmedString(data[key]),
  );
}

/**
 * Ключ-урл записи: `farkopy`, `farkopy/filter/marka-#VALUE#/model-audi`.
 * Значение root превращается в #VALUE# — «любое значение параметра».
 */
export function buildRecordMetaUrl(data: Record<string, any>): string {
  const section = asTrimmedString(data?.section);
  if (!section) return "";

  const paramKeys = paramKeysOfRecord(data);
  const restKeys = paramKeys
    .filter((key) => !ORDERED_PARAM_KEYS.includes(key))
    .sort((a, b) => a.localeCompare(b));

  const parts = [
    ...ORDERED_PARAM_KEYS.filter((key) => paramKeys.includes(key)),
    ...restKeys,
  ].map((key) => {
    const value = asTrimmedString(data[key]);
    return `${key}-${value === ROOT_VALUE ? URL_ANY_VALUE : value}`;
  });

  return parts.length > 0 ? `${section}/filter/${parts.join("/")}` : section;
}

/**
 * Ключи-урлы страницы от самого точного к самому общему.
 * Один в один алгоритм из старого getCatalog.js: на каждом уровне фильтра
 * добавляются три варианта — весь путь с #VALUE#, путь с #VALUE# плюс текущее
 * конкретное значение и путь целиком с конкретными значениями.
 */
export function buildPageMetaUrls(resolvedUrl: string): string[] {
  const urlWithoutParams = asTrimmedString(resolvedUrl).split("?")[0];
  const urls: string[] = [];
  let lastPath = "";
  let lastPathWithValues = "";
  let isFilter = false;

  urlWithoutParams.split("/").forEach((item) => {
    if (item === "filter") {
      isFilter = true;
    } else if (item && isFilter) {
      const [filterName] = item.split("-");

      urls.push(`${lastPath}/${filterName}-${URL_ANY_VALUE}`);
      urls.push(`${lastPath}/${item}`);
      urls.push(`${lastPathWithValues}/${item}`);

      lastPath = `${lastPath}/${filterName}-${URL_ANY_VALUE}`;
      lastPathWithValues = `${lastPathWithValues}/${item}`;
    } else if (item && item !== "catalog") {
      urls.push(item);
      lastPath = `${item}/filter`;
      lastPathWithValues = `${item}/filter`;
    }
  });

  urls.reverse();
  return urls;
}

/** Сортировка записи: меньше — важнее, без сортировки — в конец */
function recordSort(record: any): number {
  const sort = Number(record?.sort);
  return record?.sort == null || Number.isNaN(sort) ? Infinity : sort;
}

/** Кладёт запись по ключу, если там пусто или у неё сортировка меньше */
function setIfLowerSort(map: Map<string, any>, url: string, record: any) {
  const current = map.get(url);
  if (!current || recordSort(record) < recordSort(current)) map.set(url, record);
}

/** Записи по ключу-урлу: отдельно свой домен, отдельно root (без исключивших host) */
export function buildRecordsByUrl(records: any[], host: string) {
  const byDomain = new Map<string, any>();
  const byRoot = new Map<string, any>();

  const active = (Array.isArray(records) ? records : []).filter(
    (record) => record?.active !== false && record?.data,
  );

  active.forEach((record) => {
    const url = buildRecordMetaUrl(record.data);
    if (!url) return;

    const domain = asTrimmedString(record.data.domain);
    if (domain && domain === host) {
      setIfLowerSort(byDomain, url, record);
    } else if (domain === ROOT_DOMAIN) {
      // root-запись может быть отключена для отдельных доменов («Кроме» в панели)
      const excluded = record.data.excludeDomains;
      if (Array.isArray(excluded) && excluded.includes(host)) return;
      setIfLowerSort(byRoot, url, record);
    }
  });

  return { byDomain, byRoot };
}

/**
 * Запись меты для страницы: первый совпавший ключ-урл, сначала свой домен,
 * потом root — как в старом getCatalog.js.
 */
export function selectMetaRecord(
  records: any[],
  resolvedUrl: string,
  host: string,
) {
  const urls = buildPageMetaUrls(resolvedUrl);

  const { byDomain, byRoot } = buildRecordsByUrl(records, host);

  for (const url of urls) {
    const found = byDomain.get(url) ?? byRoot.get(url);
    if (found) return found;
  }

  return null;
}
